/**
 * AttendanceApprovalQueue — leave, overtime, missed punches and the rest, waiting
 * on the admin.
 *
 * WHY THIS EXISTS (2026-08-05)
 * ---------------------------
 * Every approval widget in the catalog was pureoa-licensed
 * (`process-approval-queue`, `my-pending-tasks`, `process-sla-status`,
 * `recent-process-activity`, `process-statistics`), so a QuTime-only tenant had NO
 * approval widget at all — while attendance approvals are that tenant's
 * highest-frequency actionable item. Their dashboard could show who was late but
 * not that four people were waiting on a leave decision.
 *
 * NAMING
 * This is "Attendance approvals" and the process one is "Process approvals". Both
 * were previously reachable as "Approval Queue", which on a bundle dashboard put
 * two identically-labelled cards side by side over different data. A single-product
 * tenant never sees the other, so the label must still be unambiguous on its own —
 * which is why the domain is in the title rather than implied by which widgets
 * happen to be present.
 *
 * A GLANCE, NOT A COMMIT
 * Same contract as ProcessApprovalQueue: rows open the approvals page rather than
 * approving in place. An approve/reject decision needs the request's details and a
 * comment box, and a mis-click from a dashboard is expensive in a domain where the
 * record is the customer's own evidence of hours worked.
 */

import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Chip, CircularProgress, List, ListItem, Paper, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../../../api';
import { approvalsApi, type Approval } from '../../../../api/attendance';
import { WidgetErrorState } from '../../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from './widgetDisplay';
import WidgetSummaryLine from './WidgetSummaryLine';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { TaskAltOutlined } from '@mui/icons-material';

function avatarColor(seed: string): string {
  const palette = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Ageing, not priority: attendance approvals carry no priority field, and the thing
 * that matters is how long somebody has been waiting for an answer about their own
 * time. Two days is the threshold because a request submitted Friday should not
 * look calm on Monday.
 */
const STALE_AFTER_MS = 2 * 24 * 60 * 60 * 1000;

function isStale(a: Approval): boolean {
  if (!a.submitted_at) return false;
  return Date.now() - Date.parse(a.submitted_at) > STALE_AFTER_MS;
}

/** The approval type, translated. Unknown values fall back to the raw code. */
function typeLabel(a: Approval, t: (k: string, o?: Record<string, unknown>) => string): string {
  // i18next returns a namespaced miss WITHOUT the namespace, so comparing against the full key never
  // matched and an unknown type showed a key path. Compare against both forms.
  const key = `dashboard:widgets.attendanceApprovals.types.${a.type}`;
  const label = t(key);
  return label === key || label === key.slice(key.indexOf(':') + 1) ? a.type : label;
}

const AttendanceApprovalQueue: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await approvalsApi.list({ status: 'pending' });
        if (cancelled) return;
        // Oldest first: the person who has waited longest is the one to answer.
        // This inverts ProcessApprovalQueue's newest-first, deliberately — a
        // process request has a priority field and a due date, an attendance
        // request has neither, so age is the only signal available.
        const sorted = [...list].sort((a, b) => {
          const at = a.submitted_at ? Date.parse(a.submitted_at) : Number.MAX_SAFE_INTEGER;
          const bt = b.submitted_at ? Date.parse(b.submitted_at) : Number.MAX_SAFE_INTEGER;
          return at - bt;
        });
        // The full list, NOT `slice(0, 8)`. The slice belongs to the render, and
        // putting it in state made the summary line count the truncated list: an
        // org with 12 waiting read "8 pending" here while the /attendance card
        // beside it read 12 — two numbers for one fact (§10.5b). The endpoint
        // takes no limit, so this length is the real total.
        setRows(sorted);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError
            ? translateApiError(e, t)
            : t('dashboard:widgets.attendanceApprovals.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) return <WidgetErrorState error={error} />;

  if (rows.length === 0) {
    return (
      <WidgetEmptyState icon={TaskAltOutlined} title={t('dashboard:widgets.attendanceApprovals.noApprovals')} />
    );
  }

  const staleCount = rows.filter(isStale).length;
  // Eight rows is what the widget's height fits. `rows` stays whole so the
  // summary line above states the real total.
  const visible = rows.slice(0, 8);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WidgetSummaryLine
        action={staleCount > 0 ? (
          <Chip
            label={t('dashboard:widgets.attendanceApprovals.waitingCount', { count: staleCount })}
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: '0.75rem' }}
          />
        ) : undefined}
      >
        {t('dashboard:widgets.attendanceApprovals.pendingCount', { count: rows.length })}
      </WidgetSummaryLine>
      <List sx={{ py: 0, flex: 1, overflow: 'auto' }}>
        {visible.map((a, i) => {
          // `WorkerRef` is { id, name, avatar?, department? } — read from the
          // generated schema, not from memory: there is no full_name here.
          const name = a.worker?.name?.trim()
            || t('dashboard:widgets.attendanceApprovals.unknownWorker');
          return (
            <ListItem
              key={a.id}
              component={Paper}
              elevation={0}
              // `/attendance/approvals` — an admin-shell route, unlike `/me/approvals`
              // which `resolveRoleLanding` redirects for every admin-tier role with
              // `replace`, so the URL would not even change (svdy-ui §12.10).
              onClick={() => navigate('/attendance/approvals')}
              sx={{ py: 0.75, mb: i === visible.length - 1 ? 0 : 0.5,
                borderRadius: 1, cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Avatar
                sx={{
                  width: 28, height: 28, mr: 1, fontSize: '0.75rem',
                  bgcolor: avatarColor(a.worker?.id ?? a.id),
                }}
              >
                {initials(a.worker?.name)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {typeLabel(a, t)}
                  {a.submitted_at
                    ? ` · ${formatRelativeTime(a.submitted_at, t, i18n.language)}`
                    : ''}
                </Typography>
              </Box>
              {isStale(a) && (
                <Chip
                  label={t('dashboard:widgets.attendanceApprovals.stale')}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                />
              )}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default AttendanceApprovalQueue;
